import type { ProgressNoteEditFormValues } from '../progressNoteEditSchema'

/**
 * PD-1516 re-dictation: render the note the provider
 * already has into plain text for the draft pipeline's `existingNote` merge
 * context, so a second whole-note dictation UPDATES the note instead of
 * regenerating it blind. Plain prose, one labelled section per populated
 * field — the model reads this, it never round-trips.
 *
 * Returns null when the note carries no content yet (first dictation): an
 * empty merge block would only dilute the prompt.
 *
 * Structured attributes (codes, status, category, intent, priority, severity,
 * onset, rank) ride in their rows because applying the regenerated draft
 * replaces the structured arrays with the model's output — anything absent
 * from this text cannot survive a re-dictation. Attribute values are the raw
 * enum tokens so the model can echo them into the tool schema verbatim; the
 * code rides as `Display (code)` and is also what the apply step dedupes on,
 * so a row whose code does not round-trip comes back as a duplicate.
 */
export function serializeNoteForMerge(values: ProgressNoteEditFormValues): string | null {
  const sections: string[] = []
  const add = (label: string, text: string | null | undefined) => {
    const t = text?.trim()
    if (t) sections.push(`${label}:\n${t}`)
  }
  const named = (display: string, code: string) =>
    [display.trim(), code.trim() && `(${code.trim()})`].filter(Boolean).join(' ')
  const noteOf = (note: string | null | undefined) => note?.trim() && `note: ${note.trim()}`

  const s = values.summary
  add('Chief complaint', s.chiefComplaint)
  add(
    'HPI',
    s.hpi
      .map((p) => p.text.trim())
      .filter(Boolean)
      .join('\n\n'),
  )
  add(
    'Review of systems',
    s.ros
      .map((r) => [r.system.trim(), r.finding.trim()].filter(Boolean).join(': '))
      .filter(Boolean)
      .join('\n'),
  )
  add(
    'Physical examination',
    values.observations
      .filter((o) => o.category !== 'vital-signs')
      .map((o) =>
        [
          [named(o.display, o.code), o.valueString?.trim()].filter(Boolean).join(': '),
          noteOf(o.note),
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .filter(Boolean)
      .join('\n'),
  )
  add(
    'Vitals',
    values.observations
      .filter((o) => o.category === 'vital-signs' && o.valueQuantity)
      .map((o) =>
        [
          `${named(o.display, o.code)}: ${o.valueQuantity?.value ?? ''} ${o.valueQuantity?.unit ?? ''}`.trim(),
          noteOf(o.note),
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add('Assessment', s.assessment)
  add(
    'Problem list',
    values.conditions
      .filter((c) => c.display.trim() || c.code.trim())
      .map((c) =>
        [
          named(c.display, c.code),
          `category: ${c.category}`,
          `status: ${c.clinicalStatus}`,
          c.severity && `severity: ${c.severity}`,
          c.onsetDateTime && `onset: ${c.onsetDateTime}`,
          c.rank != null && `rank: ${c.rank}`,
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add(
    'Medications',
    values.medications
      .filter((m) => m.display.trim() || m.code.trim())
      .map((m) =>
        [
          named(m.display, m.code),
          m.dose?.trim(),
          m.route?.trim(),
          m.frequency?.trim(),
          `status: ${m.status}`,
          `intent: ${m.intent}`,
          m.note?.trim(),
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add(
    'Allergies',
    values.allergies
      .filter((a) => a.display.trim() || a.code?.trim())
      .map((a) =>
        [
          named(a.display, a.code ?? ''),
          a.system?.trim() && `system: ${a.system.trim()}`,
          a.category && `category: ${a.category}`,
          `status: ${a.clinicalStatus}`,
          a.reaction?.trim() && `reaction: ${a.reaction.trim()}`,
          a.severity && `severity: ${a.severity}`,
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add(
    'Procedures',
    values.procedures
      .filter((pr) => pr.display.trim() || pr.code.trim())
      .map((pr) =>
        [
          named(pr.display, pr.code),
          // Status always explicit, or the model guesses one (the tool schema
          // requires it).
          `status: ${pr.status}`,
          pr.performedDateTime ? `performed: ${pr.performedDateTime}` : undefined,
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add(
    'Orders',
    values.serviceRequests
      .filter((r) => r.display.trim() || r.code.trim())
      .map((r) =>
        [
          named(r.display, r.code),
          r.system.trim() && `system: ${r.system.trim()}`,
          `category: ${r.category}`,
          `intent: ${r.intent}`,
          r.priority && `priority: ${r.priority}`,
          r.reasonText?.trim() && `reason: ${r.reasonText.trim()}`,
        ]
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  add('Plan', s.plan)
  add(
    'Follow-up',
    values.followUps
      .map((f) => f.text.trim())
      .filter(Boolean)
      .join('\n'),
  )
  add('MDM', s.mdm)

  return sections.length > 0 ? sections.join('\n\n') : null
}
